// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React from 'react';

export interface FormInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  type?: React.HTMLInputTypeAttribute;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  error?: string;
  className?: string;
  variant?: 'default' | 'light';
}

export function FormInput({
  type = 'text',
  icon,
  iconPosition,
  error,
  className = '',
  variant = 'default',
  ...rest
}: FormInputProps) {
  // Pull out disabled + style so the disabled visual is applied via inline
  // styles. Tailwind v4 only generates classes from files listed in the
  // consuming app's @source globs; ui-components isn't always included, so
  // class-based disabled styling can silently no-op. Inline styles are
  // bulletproof across every app.
  const { disabled, style: styleProp, ...inputRest } = rest;
  const hasIcon = !!icon && !!iconPosition;

  // Mirror the Dropdown's disabled look (grey bg, muted text/border,
  // not-allowed cursor) so locked/read-only fields read as disabled.
  const disabledStyle: React.CSSProperties | undefined = disabled
    ? { backgroundColor: '#F4F4F4', color: '#A3A3A3', borderColor: '#A3A3A3', cursor: 'not-allowed' }
    : undefined;
  const mergedStyle = disabledStyle ? { ...disabledStyle, ...styleProp } : styleProp;

  const borderClass = error
    ? 'border-[var(--input-border-error)]'
    : variant === 'light'
      ? 'border-[#A3A3A3] focus:border-[var(--input-border-active)]'
      : 'border-grey-04 focus:border-[var(--input-border-active)]';

  const placeholderClass = variant === 'light'
    ? 'placeholder:text-[#A3A3A3]'
    : 'placeholder:text-[#B6B6B6]';

  const inputClass = [
    'w-full h-[48px] px-md border rounded-lg bg-white outline-none transition-colors',
    'font-sans text-body-02 font-normal leading-normal text-black',
    `${placeholderClass} placeholder:font-sans placeholder:text-body-02 placeholder:font-normal placeholder:leading-normal`,
    'focus-visible:shadow-[var(--input-focus-ring)]',
    borderClass,
    hasIcon && iconPosition === 'left' ? 'pl-[44px]' : '',
    hasIcon && iconPosition === 'right' ? 'pr-[44px]' : '',
    className,
  ].filter(Boolean).join(' ');

  // Standardized error message: token-driven, matching TextInput's `.fin-input__error`
  // (#96040E / Body-03 14px / 400 / 8px gap). `block` so the top margin actually applies.
  const errorEl = error ? (
    <span className="block mt-sm font-sans text-body-03 font-normal text-[var(--input-error-text)]">
      {error}
    </span>
  ) : null;

  if (hasIcon) {
    return (
      <>
        <div className="relative">
          <input
            type={type}
            className={inputClass}
            disabled={disabled}
            style={mergedStyle}
            {...inputRest}
          />
          <span
            className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-grey-04 ${
              iconPosition === 'left' ? 'left-[16px]' : 'right-[16px]'
            }`}
          >
            {icon}
          </span>
        </div>
        {errorEl}
      </>
    );
  }

  return (
    <>
      <input
        type={type}
        className={inputClass}
        disabled={disabled}
        style={mergedStyle}
        {...inputRest}
      />
      {errorEl}
    </>
  );
}
